/**
 * @nrx/error-tracking-web — Sentry for browser / React apps.
 *
 * The browser twin of @nrx/error-tracking: same PHI scrubbing
 * (scrubPhiFromSentryEvent, from the runtime-agnostic phi-scrub module) but built
 * on @sentry/react and configured for the browser. There is no OTel trace context
 * (the browser has none) and no Secrets Manager — the DSN is public and injected
 * at build time via VITE_SENTRY_DSN.
 *
 * Usage — at the very top of each app's src/main.tsx, before createRoot():
 *   import { initSentryBrowser } from '@nrx/error-tracking-web';
 *   initSentryBrowser({
 *     dsn: import.meta.env.VITE_SENTRY_DSN,
 *     environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
 *     release: import.meta.env.VITE_SENTRY_RELEASE,
 *     app: 'admin-portal',
 *   });
 */
import {
  redactCredentialsFromUrl,
  scrubPhiFromSentryEvent,
  URL_DATA_KEYS,
  type SentryEvent,
} from '@nrx/error-tracking/phi-scrub';
import * as Sentry from '@sentry/react';

// Re-export the Sentry namespace so apps can use Sentry.ErrorBoundary etc. without
// adding @sentry/react to their own deps.
export { Sentry };

export interface InitSentryBrowserOptions {
  /** Public DSN (VITE_SENTRY_DSN). Empty/undefined ⇒ Sentry disabled (no-op). */
  readonly dsn: string | undefined;
  /** App name → set as the `app` tag so the shared nrx-frontend project filters per app. */
  readonly app: string;
  readonly environment?: string | undefined;
  readonly release?: string | undefined;
  readonly tracesSampleRate?: number;
}

const SAFARI_EXTENSION_TAB_NOT_FOUND = 'Invalid call to runtime.sendMessage(). Tab not found.';

/**
 * Safari rejects an extension content script's `runtime.sendMessage()` with this message once its
 * tab is gone, and the rejection lands in the page's unhandledrejection handler with no stack. No
 * app code calls extension APIs, so the same message WITH frames is still reported.
 */
function isSafariExtensionRejection(event: SentryEvent): boolean {
  const values = event.exception?.values ?? [];
  return (
    values.length > 0 &&
    values.every(
      ({ value, stacktrace }) => value === SAFARI_EXTENSION_TAB_NOT_FOUND && !hasFrames(stacktrace),
    )
  );
}

function hasFrames(stacktrace: unknown): boolean {
  return (
    typeof stacktrace === 'object' &&
    stacktrace !== null &&
    'frames' in stacktrace &&
    Array.isArray(stacktrace.frames) &&
    stacktrace.frames.length > 0
  );
}

function scrubOrDrop(event: SentryEvent): SentryEvent | null {
  return isSafariExtensionRejection(event) ? null : scrubPhiFromSentryEvent(event);
}

/**
 * Initialises Sentry in a browser app. No-op (returns false) when no DSN is set,
 * mirroring the backend's "missing DSN ⇒ disabled" contract so local dev and
 * un-provisioned builds boot cleanly. Returns true when Sentry was initialised.
 */
export function initSentryBrowser(options: InitSentryBrowserOptions): boolean {
  if (!options.dsn) return false;
  Sentry.init({
    dsn: options.dsn,
    environment: options.environment ?? 'sandbox',
    ...(options.release ? { release: options.release } : {}),
    tracesSampleRate: options.tracesSampleRate ?? 0.1,
    initialScope: { tags: { app: options.app } },
    // Suppress browser-extension noise: password managers (e.g. LastPass) inject
    // into form fields and throw "Object Not Found Matching Id:N, MethodName:update"
    // when those elements are removed. These are never application errors.
    ignoreErrors: [/Object Not Found Matching Id:/],
    // Cast through any — our SentryEvent is a structural subset of Sentry's Event.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    beforeSend: scrubOrDrop as any,
    // REDACT AT CAPTURE, NOT ONLY AT SEND. Keeps the credential out of the SDK's in-memory crumb
    // buffer for the whole session, reachable by anything else with page context; `beforeSend`
    // projects what actually goes on the wire. URL keys only — a session's own log trail is worth
    // keeping in memory even though none of it is allowed out.
    //
    // In a magic-link app the URL IS the credential, and `breadcrumbsIntegration` is a DEFAULT
    // integration: every navigation records `data.from`/`data.to` and every fetch records
    // `data.url`. Browser-only, hence here rather than in the runtime-agnostic scrubber — a
    // service has no navigation history.
    beforeBreadcrumb: (crumb) => {
      if (crumb.data === undefined) return crumb;
      // Rebuilt key-by-key rather than spread. Sentry types `data` as `{[key: string]: any}`, so
      // spreading it into a Record makes every downstream read an unsafe-any; iterating
      // Object.entries<unknown> narrows each value to `unknown` at the boundary instead, which is
      // what lets this run under the repo's type-aware lint with no casts and no disables.
      const data: Record<string, unknown> = {};
      for (const [key, value] of Object.entries<unknown>(crumb.data)) {
        data[key] =
          URL_DATA_KEYS.has(key) && typeof value === 'string'
            ? redactCredentialsFromUrl(value)
            : value;
      }
      return { ...crumb, data };
    },
  });
  return true;
}
